import React from 'react'
import { Route, Switch } from 'react-router'
import { connect } from 'react-redux'

import loadable from 'components/utils/lazyImport'
import { selectLoggedIn } from 'selectors/accountSelectors'
import {
  BUSINESS_TRAVELLER_NO_HEADER_ROUTES,
  BUSINESS_TRAVELLER_SALES_LANDING_ROUTE,
  BUSINESS_TRAVELLER_SUPPORT_HEADER_ROUTES,
} from 'components/App/Routes/routeConstants'
import { useTripPermissions } from 'tripPlanner/hooks/useTripPermissions'
import { LE_AGENT_HUB_COMPLETE_ACCOUNT_SETUP } from 'agentHub/routes/AgentHubRoutes'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { CHECKOUT_HEADER_PATHS } from './constants'

const HeaderMain = loadable(() => import(/* webpackChunkName: 'HeaderMain' */ 'components/App/Header/HeaderMain/HeaderMain'), <div />)
const HeaderCheckout = loadable(() => import(/* webpackChunkName: 'HeaderCheckout' */ 'components/App/Header/HeaderCheckout'), <div />)
const HeaderTripPlannerImmersive = loadable(() => import(/* webpackChunkName: 'TripPlannerImmersiveHeader' */ 'tripPlanner/components/ImmersiveHeader'), <div />)
const BusinessTravellerSupportHeader = loadable(() => import(/* webpackChunkName: 'BusinessTravellerSupportHeader' */ 'businessTraveller/components/BusinessTravellerSupportHeader'), <div />)
const BusinessTravellerGuestHeader = loadable(() => import(/* webpackChunkName: 'BusinessTravellerGuestHeader' */ 'businessTraveller/components/BusinessTravellerGuestHeader'), <div />)

const routesWithNoHeader = [
  '/:regionCode/gift-card/:code',
  '/:regionCode/trip-planner/trip',
  '/:regionCode/trip-planner/public-trip/:id',
  '/:regionCode/trip-planner/generate-trip',
  '/:regionCode/search/map',
  '/:regionCode/search/homes-and-villas/map',
  `/:regionCode${LE_AGENT_HUB_COMPLETE_ACCOUNT_SETUP}`,
  ...BUSINESS_TRAVELLER_NO_HEADER_ROUTES,
]

const stickySearchHeaderPaths = [
  '/:regionCode/hotels',
  '/:regionCode/tours',
  '/:regionCode/cruises',
  '/:regionCode/car-hire',
  '/:regionCode/experiences',
  '/:regionCode/flights',
  '/:regionCode/homes-and-villas',
  '/:regionCode/trip-planner',
  '/:regionCode/trip-planner/about',
  '/:regionCode',
]

const desktopAndLargerOnlyHeaderPaths = [
  '/:regionCode/support',
]

interface Props {
  isLoggedIn: boolean;
  tripPlannerTripId?: string;
}

function HeaderRoutes(props: Props) {
  const { isLoggedIn, tripPlannerTripId } = props
  const { canEdit } = useTripPermissions(tripPlannerTripId)

  return (
    <Switch>
      <Route path={routesWithNoHeader} />
      {!isLoggedIn && <Route exact path={BUSINESS_TRAVELLER_SALES_LANDING_ROUTE} />}
      <Route path={CHECKOUT_HEADER_PATHS} component={HeaderCheckout} />
      <Route exact path={BUSINESS_TRAVELLER_SUPPORT_HEADER_ROUTES} component={BusinessTravellerSupportHeader} />
      {!isLoggedIn && <Route component={BusinessTravellerGuestHeader} />}
      {tripPlannerTripId && canEdit && <Route>
        <HeaderTripPlannerImmersive tripId={tripPlannerTripId} />
      </Route>}
      <Route exact path={stickySearchHeaderPaths}>
        <HeaderMain stickySearch />
      </Route>
      <Route exact path={desktopAndLargerOnlyHeaderPaths}>
        <CSSBreakpoint min="desktop">
          <HeaderMain />
        </CSSBreakpoint>
      </Route>
      <Route component={HeaderMain} />
    </Switch>
  )
}

function mapStateToProps(state: App.State) {
  return {
    isLoggedIn: selectLoggedIn(state),
    tripPlannerTripId: state.tripPlanner.currentTripId,
  }
}

export default connect(mapStateToProps)(HeaderRoutes)
